import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import { IAppState } from '../redux/store/types';
import { ISaasTenant } from '../redux/saasTenants/types';
import { Tooltip, Table, TableBody, TableCell, TableContainer, TableRow, Paper, ClickAwayListener } from '@mui/material';

interface ITokenUsageState {
  showTable: boolean;
}

export class TokenUsage extends React.Component<ITokenUsageProps, ITokenUsageState> {
  constructor(props: ITokenUsageProps) {
    super(props);
    this.state = {
      showTable: false,
    };
  }

  /**
   * Format a number for display.
   */
  formatNumber = (value: number | undefined): string => {
    if (!value) return '0';
    return value.toLocaleString();
  };

  /**
   * Format an ISO date string for display.
   */
  formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  /**
   * Get total prompt tokens used for the current tenant.
   */
  getTotalPromptTokens = (): number => {
    const { currentSaasTenant } = this.props;
    if (!currentSaasTenant?.usage) return 0;
    return currentSaasTenant.usage.total_prompt_tokens || 0;
  };

  /**
   * Get total completion tokens used for the current tenant.
   */
  getTotalCompletionTokens = (): number => {
    const { currentSaasTenant } = this.props;
    if (!currentSaasTenant?.usage) return 0;
    return currentSaasTenant.usage.total_completion_tokens || 0;
  };

  /**
   * Get total tokens used for the current tenant (prompt + completion + reasoning).
   */
  getTotalTokens = (): number => {
    const { currentSaasTenant } = this.props;
    if (!currentSaasTenant?.usage) return 0;

    const prompt = currentSaasTenant.usage.total_prompt_tokens || 0;
    const completion = currentSaasTenant.usage.total_completion_tokens || 0;
    const reasoning = currentSaasTenant.usage.total_reasoning_tokens || 0;

    return prompt + completion + reasoning;
  };

  /**
   * Compute remaining credit (USD).
   *
   * Note: this can temporarily go slightly negative due to a race/double-charge scenario
   * (e.g., remaining credit was ~$0.05 and then gets reduced twice: once for the access
   * itself and again for the generated summary). When that happens, we clamp to 0 and
   * log a warning.
   */
  getRemainingCredit = (): number => {
    const { currentSaasTenant } = this.props;
    if (!currentSaasTenant) return 0;

    const remainingCredit = (currentSaasTenant.credit as number) - (currentSaasTenant.usage?.total_cost || 0);
    if (remainingCredit < 0) {
      console.warn(`Remaining credit is negative (${remainingCredit}). Clamping to 0.`);
      return 0;
    }

    return remainingCredit;
  };

  /**
   * Tooltip content shown on hover.
   */
  getTooltipContent = (): string => {
    const { t } = this.props;
    const totalTokens = this.getTotalTokens();
    return `${t('tokenUsage.totalTokens')}: ${this.formatNumber(totalTokens)}`;
  };

  /**
   * Toggle usage table visibility.
   */
  handleClick = () => {
    this.setState({ showTable: !this.state.showTable });
  };

  /**
   * Close usage table when clicking outside.
   */
  handleClickAway = () => {
    this.setState({ showTable: false });
  };

  /**
   * Render the usage detail table.
   */
  renderUsageTable = () => {
    const { currentSaasTenant, t } = this.props;
    if (!currentSaasTenant?.usage) return null;

    const usage = currentSaasTenant.usage;
    const rows = [
      { label: t('tokenUsage.promptTokens'), value: this.formatNumber(usage.total_prompt_tokens) },
      { label: t('tokenUsage.completionTokens'), value: this.formatNumber(usage.total_completion_tokens) },
      { label: t('tokenUsage.reasoningTokens'), value: this.formatNumber(usage.total_reasoning_tokens) },
      { label: t('tokenUsage.totalCost'), value: `$${usage.total_cost.toFixed(2)}` },
      { label: t('tokenUsage.usageCount'), value: this.formatNumber(usage.usage_count) },
      { label: t('tokenUsage.profilesUsed'), value: this.formatNumber(usage.profiles_used) },
      { label: t('tokenUsage.firstUsage'), value: this.formatDate(usage.first_usage) },
      { label: t('tokenUsage.lastUsage'), value: this.formatDate(usage.last_usage) },
    ];

    return (
      <div className='token-usage-table'>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align='right'>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  /**
   * Render the compact credit display and optional details table.
   */
  render() {
    const { currentSaasTenant, t } = this.props;
    const { showTable } = this.state;
    const remainingCredit = this.getRemainingCredit();

    if (!currentSaasTenant) {
      return null;
    }

    const formattedRemainingCredit = remainingCredit.toFixed(2);

    return (
      <ClickAwayListener onClickAway={this.handleClickAway}>
        <div className='token-usage-container'>
          <Tooltip title={this.getTooltipContent()} arrow placement='bottom'>
            <div className='token-usage-content' onClick={this.handleClick}>
              <span className='token-usage-text'>
                {t('tokenUsage.remainingCredit')}: <strong>${formattedRemainingCredit}</strong>
              </span>
            </div>
          </Tooltip>
          {showTable && this.renderUsageTable()}
        </div>
      </ClickAwayListener>
    );
  }
}

interface ITokenUsageProps {
  currentSaasTenant?: ISaasTenant;
  t: (key: string, options?: any) => string;
  i18n: i18n;
  tReady: boolean;
}

/**
 * Select the current authenticated tenant from Redux state.
 */
const mapStateToProps = (state: IAppState) => {
  // Find the current authenticated user from saasTenants
  const currentSaasTenant = state.saasTenants.saasTenants.find((tenant) => tenant.saas_tenant_id === state.auth.userId);
  return { currentSaasTenant };
};

const ConnectedTokenUsage = connect(mapStateToProps)(TokenUsage);
export default withTranslation()(ConnectedTokenUsage);
